import { Device } from '@goodboydigital/astro';
import { Entity, SystemInterface } from '@goodboydigital/odie';
import { Container } from 'pixi.js';

import { Shared } from '../../App';
import { Settings } from '../../AppConfig';
import { AnalyticsEvent } from '../../plugins/AnalyticsPlugin';
import { PerkAdButton } from '../../ui/buttons/PerkAdButton';
import Confetti from '../../ui/Confetti';
import { FinishPosition } from '../../ui/FinishPosition';
import { GoWidget } from '../../ui/GoWidget';
import { MessageWidget } from '../../ui/MessageWidget';
import { NicknameWidget } from '../../ui/NicknameWidget';
import { RacePoster } from '../../ui/RacePoster';
import { RaceWidget } from '../../ui/RaceWidget';
import { TrafficLightsWidget } from '../../ui/TrafficLightsWidget';
import { ValueWidget, ValueWidgetType } from '../../ui/ValueWidget';
import { Game } from '../Game';
import { SettingsButton } from './../../ui/buttons/SettingsButton';
import { OpponentLoaderWidget } from './../../ui/OpponentLoaderWidget';

export class HUDSystem implements SystemInterface
{
    public game: Game;
    public view: Container;
    public settingsButton: SettingsButton;
    public raceCoinsWidget: ValueWidget;
    public raceTimeWidget: ValueWidget;
    public raceWidget: RaceWidget;
    public coinsWidget: ValueWidget;
    public diamondWidget: ValueWidget;
    public nicknameWidget: NicknameWidget;
    public messageWidget: MessageWidget;
    public trafficLightsWidget: TrafficLightsWidget;
    public racePosterWidget: RacePoster;
    public confetti: Confetti;
    public opponentLoader: OpponentLoaderWidget;
    public goWidget: GoWidget;
    public finishPosition: FinishPosition;

    private _bonusButton?: PerkAdButton;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;

        this.view = new Container();

        this.confetti = new Confetti();
        this.view.addChild(this.confetti);

        this.racePosterWidget = new RacePoster(true);
        this.view.addChild(this.racePosterWidget.view);

        this.trafficLightsWidget = new TrafficLightsWidget();
        this.view.addChild(this.trafficLightsWidget.view);

        this.raceWidget = new RaceWidget(this.game);
        this.view.addChild(this.raceWidget.view);

        this.raceCoinsWidget = new ValueWidget(this.game, ValueWidgetType.RACE_COINS);
        this.view.addChild(this.raceCoinsWidget.view);

        this.raceTimeWidget = new ValueWidget(this.game, ValueWidgetType.RACE_TIME);
        this.view.addChild(this.raceTimeWidget.view);

        this.coinsWidget = new ValueWidget(this.game, ValueWidgetType.COINS);
        this.view.addChild(this.coinsWidget.view);

        this.diamondWidget = new ValueWidget(this.game, ValueWidgetType.DIAMONDS);
        this.view.addChild(this.diamondWidget.view);

        this.nicknameWidget = new NicknameWidget(this.game);
        this.view.addChild(this.nicknameWidget.view);

        this.messageWidget = new MessageWidget(this.game);
        this.view.addChild(this.messageWidget.view);

        this.settingsButton = new SettingsButton(this.game.app, {
            includeHome: true,
        });
        this.view.addChild(this.settingsButton.view);
        this.settingsButton.view.visible = false;

        this.opponentLoader = new OpponentLoaderWidget(this.game.app);
        this.view.addChild(this.opponentLoader.view);

        this.goWidget = new GoWidget();
        this.view.addChild(this.goWidget.view);

        this.finishPosition = new FinishPosition();
        this.view.addChild(this.finishPosition.view);

        this.game.uiView.addChild(this.view);
        this.game.onPlay.connect(() => this.onPlay());
        this.game.onIdle.connect(() => this.onIdle());
        this.game.race.onRaceCountdownStart.connect(() => this.onRaceCountdownStart());
        this.game.race.onRaceStart.connect(() => this.onRaceStart());
        this.game.race.onRaceFinish.connect(() => this.onRaceFinish());
        
        if(!Settings.browserLite && Settings.isApp())
        {
            this._bonusButton = this._createRewardButton();
        }
        
        this.resize();
    }

    public resize(): void
    {
        const hw = this.game.width / 2;
        const hh = this.game.height / 2;

        this.view.x = hw;
        this.view.y = hh;
        this.confetti.x = -hw;
        this.confetti.y = -hh;
        this.confetti.w = this.game.width;
        this.confetti.h = this.game.height;

        this.settingsButton.view.position.set(
            -hw + 90 + this.game.app.notch.left,
            -hh + 90,
        );

        this.raceWidget.top = -hh + 30;
        this.raceWidget.centerX = -30;
        this.raceTimeWidget.top = -hh + 30;
        this.raceTimeWidget.right = hw - 30 - this.game.app.notch.right;
        this.raceCoinsWidget.top = this.raceTimeWidget.bottom + 20;
        this.raceCoinsWidget.right = hw - 30 - this.game.app.notch.right;
        this.messageWidget.view.y = -hh / 2;

        this.diamondWidget.right = hw - 36 - this.game.app.notch.right;
        this.diamondWidget.top = -hh + 36;
        this.coinsWidget.right = this.diamondWidget.left - 30;
        this.coinsWidget.view.y = this.diamondWidget.view.y;
        this.nicknameWidget.view.x = -hw + 250 + this.game.app.notch.left;
        this.nicknameWidget.view.y = this.diamondWidget.view.y;

        this.trafficLightsWidget.view.x = hw * 0.6;
        this.trafficLightsWidget.view.y = -hh;
        this.racePosterWidget.view.x = hw * 0.6;
        this.racePosterWidget.view.y = -hh + 150;

        this.goWidget.view.x = 0;
        this.goWidget.view.y = -hh * 0.6;

        this.finishPosition.view.x = 0;
        this.finishPosition.view.y = -hh * 0.6;

        this.opponentLoader.view.position.set(0, 40);
            
        if(this._bonusButton)
        {
            this._bonusButton.view.x = hw;
            this._bonusButton.view.y = this.game.height - (Device.mobile ? 275 : 100);
        }
        
    }

    public update(): void
    {
        this.racePosterWidget.update();
        this.confetti.update(this.game.time.frameTime);
        this.goWidget.update(this.game.time.frameTime);
        if (!this.game.race.running || this.game.vehicle.finished)
        {
            this.coinsWidget.refresh();
            this.diamondWidget.refresh();

            return;
        }
        this.raceTimeWidget.refresh();
        this.raceWidget.refresh();
        this.raceCoinsWidget.refresh();
    }

    private onPlay(): void
    {
        this.settingsButton.view.visible = true;
        this.raceCoinsWidget.close();
        this.raceTimeWidget.close();
        this.raceWidget.close();
        this.coinsWidget.close();
        this.diamondWidget.close();
        this.nicknameWidget.close();
        this.goWidget.close();
        this.confetti.stop();

        const step = 1;
        const race = this.game.race.raceIndex;
        const position = 0;
        const raceType = this.game.race.getRaceTypeByIndex(race);
        const players = 1;
        const time = 1;
        const nemesisCount = this.game.race.getNemesisCountByIndex(race);

        if (race > 0)
        {
            this.goWidget.show(1);
            this.racePosterWidget.setup({ race, position, raceType, step, players, time, nemesisCount });
            this.racePosterWidget.setCharacterIcon(this.game.race.getNemesisOpponent());
            this.racePosterWidget.open();
            if (!Settings.rec) setTimeout(() => {
                if(this._bonusButton)
                {
                    this._bonusButton.show(true)
                }
            }, 500);
        }

        // Put the bonus button atop of everything else, otherwise this will be behind
        // the touch controls and will loose interactivity
        if(this._bonusButton)
        {
            this.game.app.stage.stage.addChild(this._bonusButton.view);
        }
    }

    private onIdle(): void
    {
        this.settingsButton.view.visible = false;
        this.raceCoinsWidget.close();
        this.raceTimeWidget.close();
        this.raceWidget.close();
        this.racePosterWidget.close();
        this.trafficLightsWidget.close();
        this.opponentLoader.close();
        this.goWidget.close();
        this.confetti.stop();
        if(this._bonusButton)
        {
            this._bonusButton.hide(true);
        }
    }

    private async onRaceCountdownStart(): Promise<void>
    {
        if(this._bonusButton)
        {
            this._bonusButton.hide(true);
        }
        this.racePosterWidget.close();
        this.goWidget.close();
    }

    private async onRaceStart(): Promise<void>
    {
        this.resize();
        this.settingsButton.view.visible = true;
        this.racePosterWidget.close();
        await this.raceWidget.open();
        await this.raceTimeWidget.open();
        await this.raceCoinsWidget.open();
    }

    private onRaceFinish(): void
    {
        this.raceWidget.close();
        this.racePosterWidget.close();
        this.goWidget.close();
        this.settingsButton.view.visible = false;
        this.confetti.play();
    }

    public async showCoinsAndDiamonds(): Promise<void>
    {
        // Open the nickname widget only if player does not have any rank points or is in tutorial race
        if (!Settings.browserLite && this.game.app.user.onTutorial())
        {
            this.nicknameWidget.open();
        }
        await this.coinsWidget.open();
        
        this.diamondWidget.open(); 
    }
    
    private _createRewardButton(): PerkAdButton
    {
        const button = new PerkAdButton({ text: 'preRaceScreen_bonus' });
        button.setAdIconVisible(this.game.app.ads.config.preRaceRewardAds && this.game.app.ads.canShowAd());

        this.view.addChild(button.view);
        button.onPress.connect(() => this.onRewardButtonPressed());

        Device.mobile && button.view.scale.set(0.85);

        return button;
    }
    
    public async onRewardButtonPressed(): Promise<void>
    {
        if(this._bonusButton){
            if (Shared.game.paused)
            {
                Shared.game.hud.settingsButton.close();
            }
            
            const reward = this.game.app.reward.getRandomReward(this.game.app.reward.getPreRacePerks());
            this.game.app.analytics.trackEvent(AnalyticsEvent.RACE_RACEPERK_CLICK, { game_item: `racereward|${reward.id}` });
            this.game.pause();
            this._bonusButton.hide(false);
            await this.game.app.reward.claimPreRacePerk(reward, this._bonusButton.adIcon.visible);
            console.log("resume")
            this.game.resume();
        }
        
    }
}
